import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Observable, catchError, map, of } from 'rxjs';
import { ApiResponse } from '@interfaces/httpResponses';
import { Company } from '@interfaces/Company';
import {
  CompanySubscription,
  SubscriptionViewState,
} from '@interfaces/Subscription';
import { AccountService } from './account.service';

/** At or below this many days left the trial notice switches to warning. */
export const TRIAL_WARNING_DAYS = 3;

const NO_BANNER: SubscriptionViewState = {
  banner: 'none',
  readOnly: false,
  daysLeft: null,
  checkoutUrl: environment.subscriptionCheckoutUrl,
};

/**
 * Pure decision of which notice to show. Tolerant by design: a missing or
 * unknown `subscription` (old backend) is treated as an active account.
 */
export function resolveSubscriptionView(
  subscription: CompanySubscription | null | undefined,
  fallbackCheckoutUrl: string = environment.subscriptionCheckoutUrl,
): SubscriptionViewState {
  if (!subscription || typeof subscription !== 'object') {
    return { ...NO_BANNER, checkoutUrl: fallbackCheckoutUrl };
  }

  const checkoutUrl = subscription.checkoutUrl?.trim() || fallbackCheckoutUrl;

  if (subscription.readOnly === true || subscription.status === 'inactive') {
    return { banner: 'read-only', readOnly: true, daysLeft: 0, checkoutUrl };
  }

  if (subscription.status === 'trial') {
    const daysLeft =
      typeof subscription.daysLeft === 'number'
        ? Math.max(0, subscription.daysLeft)
        : null;
    if (daysLeft === null) {
      return { ...NO_BANNER, checkoutUrl };
    }
    return {
      banner: daysLeft <= TRIAL_WARNING_DAYS ? 'trial-warning' : 'trial',
      readOnly: false,
      daysLeft,
      checkoutUrl,
    };
  }

  return { ...NO_BANNER, checkoutUrl };
}
import { blockingLoaderHeaders } from '../../interceptors/loader.interceptor';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  /** Current notice state, derived from the company stored in the session. */
  readonly view$: Observable<SubscriptionViewState> =
    this.accountService.company$.pipe(
      map((company) => resolveSubscriptionView(company?.subscription)),
    );

  constructor(
    private readonly accountService: AccountService,
    private readonly http: HttpClient,
  ) {}

  /**
   * Re-reads the subscription from the API so a workshop activated by the
   * admin does not need to log in again. Silent on failure: keeps what is
   * stored. A fresh `readOnly: false` replaces the flag set by a past 402.
   */
  refresh(): Observable<void> {
    const company = this.accountService.companyData;
    if (!company?._id) return of(undefined);

    return this.http
      .get<
        ApiResponse<{
          subscription?: CompanySubscription;
          isPlatformAdmin?: boolean;
        }>
      >(`${environment.apiUrl}user/account/subscription`, {
        headers: blockingLoaderHeaders({ message: 'Cargando tu cuenta...' }),
      })
      .pipe(
        map((response) => {
          const { subscription, isPlatformAdmin } = response?.payload ?? {};
          if (subscription) {
            this.accountService.setCompanyData({ ...company, subscription });
          }
          if (typeof isPlatformAdmin === 'boolean') {
            this.accountService.setUserData({
              ...this.accountService.userData,
              isPlatformAdmin,
            });
          }
        }),
        catchError(() => of(undefined)),
      );
  }

  get checkoutUrl(): string {
    return resolveSubscriptionView(this.accountService.companyData.subscription)
      .checkoutUrl;
  }

  openCheckout(): void {
    window.open(this.checkoutUrl, '_blank', 'noopener');
  }

  /**
   * The API answered 402 SUBSCRIPTION_REQUIRED: the trial ended while the
   * session was open. Flip the stored state so the read-only banner shows
   * without waiting for the next login.
   */
  markReadOnly(): void {
    const company: Company = this.accountService.companyData;
    if (!company?._id || company.subscription?.readOnly) return;

    this.accountService.setCompanyData({
      ...company,
      subscription: {
        status: company.subscription?.status ?? 'inactive',
        trialEndsAt: company.subscription?.trialEndsAt ?? null,
        daysLeft: 0,
        checkoutUrl: company.subscription?.checkoutUrl ?? null,
        readOnly: true,
      },
    });
  }
}
